import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useNoizaMetrics, DateRange } from '../../lib/noiza-metrics-hooks';
import { formatCurrency } from '../../lib/utils';
import { TrendingUpIcon, ShoppingCartIcon, ReceiptIcon, StoreIcon, UsersIcon, UserCheckIcon, ArrowLeftIcon, ActivityIcon, RefreshCwIcon, Loader2Icon, CalendarIcon, FilterIcon, ClockIcon, AwardIcon, AlertTriangleIcon, ChevronDownIcon, FileTextIcon } from 'lucide-react';
// ─── Helpers ───────────────────────────────────────────────
// All date helpers return Costa Rica local date (UTC-6).
// Using UTC `.toISOString()` would give the wrong day for users in CR
// late at night (it'd flip to tomorrow's date).
function getCRDate(d: Date = new Date()): Date {
  // Convert to CR time by adjusting the timestamp by the difference
  // between UTC and CR offsets.
  const utcMs = d.getTime() + d.getTimezoneOffset() * 60000;
  return new Date(utcMs - 6 * 60 * 60000);
}
function formatYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function formatCompactCurrency(value: number): string {
  if (value >= 1000000) return `₡${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `₡${(value / 1000).toFixed(0)}k`;
  return formatCurrency(value);
}
function formatDayLabel(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${d} ${months[m - 1]}`;
  } catch {
    return dateStr;
  }
}
function normalizeChannel(ch: string): string {
  const lower = (ch || '').toLowerCase();
  if (lower.includes('qr') || lower.includes('self')) return 'QR';
  if (lower.includes('waiter') || lower.includes('mesero')) return 'Mesero';
  if (lower.includes('web') || lower.includes('online')) return 'Web';
  if (lower.includes('caja') || lower.includes('cashier')) return 'Caja';
  return ch || 'Otro';
}
const CHANNEL_COLORS: Record<string, string> = {
  QR: '#10b981',
  Mesero: '#6366f1',
  Web: '#f59e0b',
  Caja: '#8b5cf6',
  Otro: '#6b7280'
};
function getDaysAgo(days: number): string {
  const d = getCRDate();
  d.setDate(d.getDate() - days);
  return formatYMD(d);
}
function getToday(): string {
  return formatYMD(getCRDate());
}
function getFirstDayOfMonth(): string {
  const d = getCRDate();
  d.setDate(1);
  return formatYMD(d);
}
// ─── Dark Tooltip ──────────────────────────────────────────
function DarkTooltip({
  active,
  payload,
  label,
  isCurrency
}: any) {
  if (!active || !payload?.length) return null;
  return <div className="bg-white/10 backdrop-blur-xl border border-white/10 text-white px-4 py-3 rounded-xl shadow-2xl text-sm z-50" data-id="element-5437" data-mp="2eo8k3xycngji">
      <p className="font-medium text-white/60 mb-1.5 text-xs uppercase tracking-wider" data-id="element-5438" data-mp="2ameo42z3n0po">
        {label}
      </p>
      {payload.map((entry: any, i: number) => <p key={i} className="flex items-center gap-2" data-id="element-5439" data-mp="2jncuut0x9mby" data-mpi={i}>
          <span className="w-2 h-2 rounded-full" style={{
        backgroundColor: entry.color || entry.fill
      }} data-id="element-5440" data-mp="2jlrz502vfxci" />
          <span className="text-white/50" data-id="element-5441" data-mp="1r08nihecwvx0">{entry.name}:</span>
          <span className="font-semibold text-white" data-id="element-5442" data-mp="25ntmq0842azn">
            {isCurrency ? formatCurrency(Number(entry.value ?? 0)) : Number(entry.value ?? 0).toLocaleString()}
          </span>
        </p>)}
    </div>;
}
// ─── KPI Card ──────────────────────────────────────────────
interface KpiCardProps {
  label: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  accentColor: string;
}
function KpiCard({
  label,
  value,
  subtitle,
  icon,
  accentColor
}: KpiCardProps) {
  return <div className="relative overflow-hidden rounded-2xl bg-[#12121a] border border-white/[0.06] p-5 group" data-id="element-5443" data-mp="gfm9jgkt5by2">
      <div className={`absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-[0.07] -translate-y-8 translate-x-8`} style={{
      backgroundColor: accentColor
    }} data-id="element-5444" data-mp="kgxydjvuxt57" />
      <div className="relative" data-id="element-5445" data-mp="u70rdk2zb73j">
        <div className="flex items-center justify-between mb-4" data-id="element-5446" data-mp="ua6g2y7p46yq">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
          backgroundColor: `${accentColor}15`
        }} data-id="element-5447" data-mp="z5lv3thfw8wq">
            {icon}
          </div>
        </div>
        <p className="text-[13px] font-medium text-white/40 uppercase tracking-wider" data-id="element-5448" data-mp="1858s8334ysjz">
          {label}
        </p>
        <p className="text-3xl font-bold text-white mt-1 tracking-tight" data-id="element-5449" data-mp="39yok13aymdgj">
          {value}
        </p>
        {subtitle && <p className="text-xs text-white/30 mt-1" data-id="element-5450" data-mp="32fp2l3726vzt">{subtitle}</p>}
      </div>
    </div>;
}
// ─── Main Page ─────────────────────────────────────────────
export function NoizaMetricsPage() {
  const [restaurantId, setRestaurantId] = useState<string>('all');
  const [dateRange, setDateRange] = useState<DateRange>({
    from: getDaysAgo(30),
    to: getToday()
  });
  const [datePreset, setDatePreset] = useState<string>('30d');
  const {
    data,
    loading,
    error,
    refetch,
    restaurants
  } = useNoizaMetrics(restaurantId, dateRange);
  const handleDatePreset = (preset: string) => {
    setDatePreset(preset);
    const to = getToday();
    let from = to;
    if (preset === 'all') from = '2020-01-01';else if (preset === 'today') from = to;else if (preset === '7d') from = getDaysAgo(7);else if (preset === '30d') from = getDaysAgo(30);else if (preset === 'month') from = getFirstDayOfMonth();
    setDateRange({
      from,
      to
    });
  };
  // Derived Data for Charts
  const dailyChartData = useMemo(() => {
    const source = data.salesDaily.length > 0 ? data.salesDaily : data.gmvDaily;
    // Aggregate by day if multiple restaurants
    const aggregated = source.reduce((acc: any, curr: any) => {
      const day = curr.day;
      if (!acc[day]) acc[day] = {
        day,
        label: formatDayLabel(day),
        gmv: 0,
        orders: 0
      };
      acc[day].gmv += curr.gmv;
      acc[day].orders += curr.orders;
      return acc;
    }, {});
    return Object.values(aggregated).sort((a: any, b: any) => a.day.localeCompare(b.day));
  }, [data.salesDaily, data.gmvDaily]);
  const hourlyChartData = useMemo(() => {
    const aggregated = data.salesHourly.reduce((acc: any, curr: any) => {
      const hour = curr.hour;
      if (!acc[hour]) acc[hour] = {
        hour,
        label: `${hour}:00`,
        gmv: 0,
        orders: 0
      };
      acc[hour].gmv += curr.gmv;
      acc[hour].orders += curr.orders;
      return acc;
    }, {});
    return Object.values(aggregated).sort((a: any, b: any) => Number(a.hour) - Number(b.hour));
  }, [data.salesHourly]);
  const channelChartData = useMemo(() => {
    return data.channelDistribution.map(d => ({
      name: normalizeChannel(d.channel),
      value: d.orders,
      pct: d.pct,
      color: CHANNEL_COLORS[normalizeChannel(d.channel)] || '#6b7280'
    })).filter(d => d.value > 0);
  }, [data.channelDistribution]);
  const totalChannelOrders = channelChartData.reduce((sum, d) => sum + d.value, 0);
  const topRestaurantsChartData = useMemo(() => {
    return data.gmvByRestaurant.slice(0, 5).map(r => ({
      name: String(r.restaurant_name || '').length > 15 ? String(r.restaurant_name || '').substring(0, 15) + '...' : String(r.restaurant_name || ''),
      fullName: r.restaurant_name,
      gmv: r.gmv
    }));
  }, [data.gmvByRestaurant]);
  const topProductsChartData = useMemo(() => {
    return data.topProducts.slice(0, 5).map(p => ({
      name: String(p.product_name || '').length > 15 ? String(p.product_name || '').substring(0, 15) + '...' : String(p.product_name || ''),
      fullName: p.product_name,
      revenue: p.revenue,
      quantity: p.quantity
    }));
  }, [data.topProducts]);
  // Insights
  const insights = useMemo(() => {
    let bestDay = {
      day: '-',
      val: 0
    };
    let worstDay = {
      day: '-',
      val: Infinity
    };
    dailyChartData.forEach((d: any) => {
      if (d.gmv > bestDay.val) bestDay = {
        day: d.label,
        val: d.gmv
      };
      if (d.gmv < worstDay.val && d.gmv > 0) worstDay = {
        day: d.label,
        val: d.gmv
      };
    });
    let bestHour = {
      hour: '-',
      val: 0
    };
    let worstHour = {
      hour: '-',
      val: Infinity
    };
    hourlyChartData.forEach((d: any) => {
      if (d.gmv > bestHour.val) bestHour = {
        hour: d.label,
        val: d.gmv
      };
      if (d.gmv < worstHour.val && d.gmv > 0) worstHour = {
        hour: d.label,
        val: d.gmv
      };
    });
    let bestRest = data.gmvByRestaurant[0];
    let worstRest = data.gmvByRestaurant[data.gmvByRestaurant.length - 1];
    return {
      bestDay,
      worstDay,
      bestHour,
      worstHour,
      bestRest,
      worstRest
    };
  }, [dailyChartData, hourlyChartData, data.gmvByRestaurant]);
  return <div className="min-h-screen bg-[#0a0a0f] text-white pb-20" data-id="element-5451" data-mp="shnhrr8cqh01">
      {/* ─── Header ─────────────────────────────────────── */}
      <header className="sticky top-0 z-50 bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-white/[0.06]" data-id="element-5452" data-mp="njvfc3a2zchl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-5453" data-mp="iifw8grgrg1u">
          <div className="flex items-center gap-4" data-id="element-5454" data-mp="9k2ugol8jqhj">
            <Link to="/admin/enrollment" className="w-9 h-9 rounded-xl bg-white/[0.06] flex items-center justify-center hover:bg-white/[0.1] transition-colors" data-id="element-5455" data-mp="9kchc3azhhj0">
              <ArrowLeftIcon className="w-4 h-4 text-white/60" data-id="element-5456" data-mp="13nzwx5pmkv4p" />
            </Link>
            <div data-id="element-5457" data-mp="1iburysr3vbky">
              <div className="flex items-center gap-2.5" data-id="element-5458" data-mp="1ice6dzhjrhu8">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20" data-id="element-5459" data-mp="1ddt845luv88f">
                  <ActivityIcon className="w-4 h-4 text-white" data-id="element-5460" data-mp="kpf8jlf4c3ur" />
                </div>
                <h1 className="text-lg font-bold tracking-tight" data-id="element-5461" data-mp="kri56oas0v8g">NOIZA</h1>
                <span className="text-xs font-medium text-white/30 bg-white/[0.06] px-2 py-0.5 rounded-md" data-id="element-5462" data-mp="zo98v9rhgt3v">
                  Executive Dashboard
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-5463" data-mp="2b3xvu14qls4o">
            <button onClick={refetch} disabled={loading} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] transition-colors text-sm text-white/60 hover:text-white/80 disabled:opacity-50" data-id="element-5464" data-mp="263jqiquiswac">
              {loading ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-5465" data-mp="24mwcsmwac46g" /> : <RefreshCwIcon className="w-4 h-4" data-id="element-5466" data-mp="28o7wycwe3x79" />}
              <span className="hidden sm:inline" data-id="element-5467" data-mp="2wd0vmh4ecj5k">Actualizar</span>
            </button>
          </div>
        </div>
      </header>

      {/* ─── Filters Bar ────────────────────────────────── */}
      <div className="bg-[#12121a] border-b border-white/[0.06]" data-id="element-5468" data-mp="2nkfef5geykiu">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-id="element-5469" data-mp="2nk733hzxvn9r">
          <div className="flex items-center gap-3" data-id="element-5470" data-mp="3fbmx3x3tsca0">
            <FilterIcon className="w-4 h-4 text-white/40" data-id="element-5471" data-mp="3fatpktx1liqt" />
            <div className="relative" data-id="element-5472" data-mp="3k23dnq2y0e0l">
              <select value={restaurantId} onChange={e => setRestaurantId(e.target.value)} className="appearance-none bg-white/[0.06] border border-white/[0.1] rounded-lg pl-3 pr-8 py-1.5 text-sm text-white focus:outline-none focus:border-amber-500/50 transition-colors" data-id="element-5473" data-mp="3k34w4fmag2sc">
                <option value="all" data-id="element-5474" data-mp="3kzgqemv19fd3">Todos los restaurantes</option>
                {restaurants.map(r => <option key={r.id} value={r.id} data-id="element-5475" data-mp="3azg4qu9oyn5q">
                    {r.name}
                  </option>)}
              </select>
              <ChevronDownIcon className="w-4 h-4 text-white/40 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-5476" data-mp="367y2m2vxmgj6" />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap pb-1 sm:pb-0" data-id="element-5477" data-mp="2x8l0xwe1uw90">
            <CalendarIcon className="w-4 h-4 text-white/40 mr-1" data-id="element-5478" data-mp="2x68cr1hp7pni" />
            {[{
            id: 'all',
            label: 'Todo'
          }, {
            id: 'today',
            label: 'Hoy'
          }, {
            id: '7d',
            label: '7 días'
          }, {
            id: '30d',
            label: '30 días'
          }, {
            id: 'month',
            label: 'Mes actual'
          }].map(preset => <button key={preset.id} onClick={() => handleDatePreset(preset.id)} className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${datePreset === preset.id ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-white/[0.03] text-white/60 border border-white/[0.05] hover:bg-white/[0.08]'}`} data-id="element-5479" data-mp="2s14rxg58ulxd">
                {preset.label}
              </button>)}

            <div className="h-5 w-px bg-white/10 mx-1" data-id="element-5480" data-mp="1bo69oshx6h0t" />

            <div className="flex items-center gap-2" data-id="element-5481" data-mp="1gnskywe6tn6z">
              <label className="text-xs text-white/40" data-id="element-5482" data-mp="1gmhegotcy1c6">Desde</label>
              <input type="date" value={dateRange.from} onChange={e => {
              setDatePreset('custom');
              setDateRange(prev => ({
                ...prev,
                from: e.target.value
              }));
            }} className="bg-white/[0.06] border border-white/[0.1] rounded-lg px-2 py-1.5 text-sm text-white focus:outline-none focus:border-amber-500/50 transition-colors [color-scheme:dark]" data-id="element-5483" data-mp="3icg8lu4i4ok0" />
              <label className="text-xs text-white/40" data-id="element-5484" data-mp="wgkub52f9c8">Hasta</label>
              <input type="date" value={dateRange.to} onChange={e => {
              setDatePreset('custom');
              setDateRange(prev => ({
                ...prev,
                to: e.target.value
              }));
            }} className="bg-white/[0.06] border border-white/[0.1] rounded-lg px-2 py-1.5 text-sm text-white focus:outline-none focus:border-amber-500/50 transition-colors [color-scheme:dark]" data-id="element-5485" data-mp="evg9vjwhpq8x" />
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-id="element-5486" data-mp="hhh0bac0xgvo">
        {error && <div className="rounded-2xl bg-red-500/10 border border-red-500/20 p-4 text-sm text-red-400 flex items-center gap-3" data-id="element-5487" data-mp="mfihfppw364i">
            <AlertTriangleIcon className="w-5 h-5" data-id="element-5488" data-mp="8fg32dgsiuu0" />
            {error}
          </div>}

        {loading ? <div className="space-y-8" data-id="element-5489" data-mp="dgnb8dmuhw17">
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-id="element-5490" data-mp="3unpef5tvcy0w">
              {Array.from({
            length: 6
          }).map((_, i) => <div key={i} className="h-32 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-5491" data-mp="3j8yao04rpdn" data-mpi={i} />)}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-5492" data-mp="1xunnn3p8bmsv">
              <div className="lg:col-span-2 h-80 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-5493" data-mp="1wea3syf5tlzy" />
              <div className="h-80 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-5494" data-mp="1redxutmwjkhr" />
            </div>
          </div> : <>
            {/* ─── KPI Cards ──────────────────────────── */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-id="element-5495" data-mp="1if0qq7m93807">
              <KpiCard label="GMV Total" value={formatCompactCurrency(Number(data.gmv.total_gmv ?? 0))} subtitle="Ingresos brutos" icon={<TrendingUpIcon className="w-5 h-5 text-emerald-400" data-id="element-5497" data-mp="25eoru5jidf75" />} accentColor="#10b981" data-id="element-5496" data-mp="2f489j7vhj30f" />
              <KpiCard label="Órdenes" value={Number(data.gmv.total_orders ?? 0).toLocaleString()} subtitle="Total procesadas" icon={<ShoppingCartIcon className="w-5 h-5 text-blue-400" data-id="element-5499" data-mp="2t4j2cggcuro8" />} accentColor="#3b82f6" data-id="element-5498" data-mp="21dwjdeignebx" />
              <KpiCard label="Ticket Promedio" value={formatCurrency(Number(data.gmv.avg_ticket ?? 0))} subtitle="Por orden" icon={<ReceiptIcon className="w-5 h-5 text-amber-400" data-id="element-5501" data-mp="oltenlebq2ef" />} accentColor="#f59e0b" data-id="element-5500" data-mp="fmogig6ocb2s" />
              <KpiCard label="Clientes Únicos" value={Number(data.uniqueCustomers ?? 0).toLocaleString()} subtitle="Compradores distintos" icon={<UsersIcon className="w-5 h-5 text-cyan-400" data-id="element-5503" data-mp="13ish6uubj521" />} accentColor="#06b6d4" data-id="element-5502" data-mp="tmh2jy79a34f" />
              <KpiCard label="Recurrentes" value={Number(data.repeatCustomers.repeat_count ?? 0).toLocaleString()} subtitle={Number(data.repeatCustomers.repeat_pct ?? 0) > 0 ? `${Math.round(Number(data.repeatCustomers.repeat_pct ?? 0))}% del total` : 'Clientes que regresan'} icon={<UserCheckIcon className="w-5 h-5 text-pink-400" data-id="element-5505" data-mp="s7tm3o35zick" />} accentColor="#ec4899" data-id="element-5504" data-mp="18blkdea525c0" />
              <KpiCard label="Autoservicio" value={`${Math.round(Number(data.selfServicePct.self_service_pct ?? 0))}%`} subtitle={`Mesero: ${Math.round(Number(data.selfServicePct.waiter_pct ?? 0))}%`} icon={<StoreIcon className="w-5 h-5 text-purple-400" data-id="element-5507" data-mp="egolaio28k0n" />} accentColor="#8b5cf6" data-id="element-5506" data-mp="1684ewwo1b70d" />
            </div>

            {/* ─── Insights Section ───────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5508" data-mp="nautj9z5fjgh">
              <div className="rounded-2xl bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/20 p-4 flex items-start gap-4" data-id="element-5509" data-mp="ih8e5a1lp1gz">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0" data-id="element-5510" data-mp="3mw85ytao0u43">
                  <AwardIcon className="w-5 h-5 text-emerald-400" data-id="element-5511" data-mp="3crrqw250fh1c" />
                </div>
                <div data-id="element-5512" data-mp="3hirq5e1lqb70">
                  <h3 className="text-sm font-semibold text-emerald-400" data-id="element-5513" data-mp="3hj9wx4lw47t6">
                    Mejor Día
                  </h3>
                  <p className="text-white font-medium mt-1" data-id="element-5514" data-mp="32vosn7da42jo">
                    {insights.bestDay.day}
                  </p>
                  <p className="text-xs text-white/50 mt-0.5" data-id="element-5515" data-mp="37qvo5rwea0xi">
                    {formatCurrency(insights.bestDay.val)}
                  </p>
                </div>
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20 p-4 flex items-start gap-4" data-id="element-5516" data-mp="4bq7mo89sgmt">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0" data-id="element-5517" data-mp="3qj6l754db4np">
                  <ClockIcon className="w-5 h-5 text-blue-400" data-id="element-5518" data-mp="2a4esss781xee" />
                </div>
                <div data-id="element-5519" data-mp="2f4t4qlby1rhp">
                  <h3 className="text-sm font-semibold text-blue-400" data-id="element-5520" data-mp="2f5c9t1ghwleb">
                    Hora Pico
                  </h3>
                  <p className="text-white font-medium mt-1" data-id="element-5521" data-mp="1exqcrmhycexu">
                    {insights.bestHour.hour}
                  </p>
                  <p className="text-xs text-white/50 mt-0.5" data-id="element-5522" data-mp="15zepnuganza5">
                    {formatCurrency(insights.bestHour.val)}
                  </p>
                </div>
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/20 p-4 flex items-start gap-4" data-id="element-5523" data-mp="1b02ficssea2s">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0" data-id="element-5524" data-mp="1113d3bym7ao8">
                  <StoreIcon className="w-5 h-5 text-amber-400" data-id="element-5525" data-mp="na84m0s12vl6" />
                </div>
                <div data-id="element-5526" data-mp="rb1q6nhzzyaw">
                  <h3 className="text-sm font-semibold text-amber-400" data-id="element-5527" data-mp="rai733gm33va">
                    Top Restaurante
                  </h3>
                  <p className="text-white font-medium mt-1 truncate max-w-[150px]" data-id="element-5528" data-mp="i9kav7cf00n9">
                    {insights.bestRest?.restaurant_name || '-'}
                  </p>
                  <p className="text-xs text-white/50 mt-0.5" data-id="element-5529" data-mp="3uv8yjal7fx7n">
                    {insights.bestRest ? formatCurrency(insights.bestRest.gmv) : '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* ─── Charts Row 1 ───────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-5530" data-mp="hmcyzhjq2uon">
              {/* GMV Daily Chart */}
              <div className="lg:col-span-2 rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-5531" data-mp="co33t6xzzu31">
                <div className="flex items-center justify-between mb-6" data-id="element-5532" data-mp="rcz9wyghux10">
                  <div data-id="element-5533" data-mp="mdcsectmuume">
                    <h2 className="text-sm font-semibold text-white/80" data-id="element-5534" data-mp="md32ep8l8ngp">
                      GMV Diario
                    </h2>
                    <p className="text-xs text-white/30 mt-0.5" data-id="element-5535" data-mp="10dnl7lei6ja7">
                      Ingresos brutos por día
                    </p>
                  </div>
                </div>
                {dailyChartData.length === 0 ? <div className="h-64 flex items-center justify-center text-white/20 text-sm" data-id="element-5536" data-mp="13cw3px8ijkog">
                    Sin datos
                  </div> : <div className="h-64" data-id="element-5537" data-mp="1dbbzidihji4a">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-5538" data-mp="1i3mzfgd873wu">
                      <AreaChart data={dailyChartData} margin={{
                  top: 4,
                  right: 4,
                  left: 0,
                  bottom: 0
                }} data-id="element-5539" data-mp="1i5pwulgpjzbl">
                        <defs data-id="element-5540" data-mp="1w1bl2qf4af2y">
                          <linearGradient id="gmvGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-5541" data-mp="2130uy2qlsmpg">
                            <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} data-id="element-5542" data-mp="6oq4jpud1khk" />
                            <stop offset="100%" stopColor="#10b981" stopOpacity={0} data-id="element-5543" data-mp="2f6pzhmk9nbhy" />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} data-id="element-5544" data-mp="32wapgpeu790q" />
                        <XAxis dataKey="label" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={{
                    stroke: 'rgba(255,255,255,0.06)'
                  }} interval="preserveStartEnd" data-id="element-5545" data-mp="2tv2pm2woa04j" />
                        <YAxis tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={formatCompactCurrency} data-id="element-5546" data-mp="367n028qli579" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-5548" data-mp="3p3mvjtis7qvl" />} data-id="element-5547" data-mp="3p57r9mgu1fv1" />
                        <Area type="monotone" dataKey="gmv" name="GMV" stroke="#10b981" strokeWidth={2} fill="url(#gmvGrad)" data-id="element-5549" data-mp="3b557tvsvmedo" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>}
              </div>

              {/* Channel Distribution */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-5550" data-mp="1x7ellgwqerxn">
                <div className="mb-4" data-id="element-5551" data-mp="1s8l83ohg30xh">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-5552" data-mp="266rvubsojkjf">
                    Canales
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-5553" data-mp="2188b2eptoesn">
                    Distribución de pedidos
                  </p>
                </div>
                {channelChartData.length === 0 ? <div className="h-48 flex items-center justify-center text-white/20 text-sm" data-id="element-5554" data-mp="19haidanggu76">
                    Sin datos
                  </div> : <>
                    <div className="h-48" data-id="element-5555" data-mp="1ifmgdxqniu9n">
                      <ResponsiveContainer width="100%" height="100%" data-id="element-5556" data-mp="1ieumcrcpd4lh">
                        <PieChart data-id="element-5557" data-mp="upiga0co9cne">
                          <Pie data={channelChartData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value" strokeWidth={0} data-id="element-5558" data-mp="ps04813cyhb6">
                            {channelChartData.map((entry, i) => <Cell key={i} fill={entry.color} data-id="element-5559" data-mp="34l8joqvu0s9h" data-mpi={i} />)}
                          </Pie>
                          <Tooltip content={({
                      active,
                      payload
                    }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      const pct = totalChannelOrders > 0 ? Math.round(d.value / totalChannelOrders * 100) : 0;
                      return <div className="bg-white/10 backdrop-blur-xl border border-white/10 text-white px-3 py-2 rounded-xl shadow-2xl text-sm" data-id="element-5561" data-mp="1kp2reci9l5zz">
                                  <p className="font-medium" data-id="element-5562" data-mp="1btv475n1gzx5">{d.name}</p>
                                  <p className="text-white/50" data-id="element-5563" data-mp="16vtlwmxft4mq">
                                    {Number(d.value ?? 0).toLocaleString()}{' '}
                                    pedidos ({pct}%)
                                  </p>
                                </div>;
                    }} data-id="element-5560" data-mp="2bugtca1g6xu2" />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-2 mt-2" data-id="element-5564" data-mp="nyk234o79p4y">
                      {channelChartData.map(entry => {
                  const pct = totalChannelOrders > 0 ? Math.round(entry.value / totalChannelOrders * 100) : 0;
                  return <div key={entry.name} className="flex items-center justify-between" data-id="element-5565" data-mp="1w6k2g49352cw">
                            <div className="flex items-center gap-2" data-id="element-5566" data-mp="1md3br948w3dg">
                              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
                        backgroundColor: entry.color
                      }} data-id="element-5567" data-mp="18en2baezzyrm" />
                              <span className="text-sm text-white/60" data-id="element-5568" data-mp="13f0gicl3ta77">
                                {entry.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-3" data-id="element-5569" data-mp="2xtl00lxouzp0">
                              <span className="text-sm font-semibold text-white/90" data-id="element-5570" data-mp="2xrgvc1rytwno">
                                {Number(entry.value ?? 0).toLocaleString()}
                              </span>
                              <span className="text-xs text-white/30 w-10 text-right" data-id="element-5571" data-mp="h5a9lb3335qj">
                                {pct}%
                              </span>
                            </div>
                          </div>;
                })}
                    </div>
                  </>}
              </div>
            </div>

            {/* ─── Charts Row 2 ───────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-5572" data-mp="1evomwt33lury">
              {/* Hourly Sales */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-5573" data-mp="19vsiisqho1bv">
                <div className="mb-6" data-id="element-5574" data-mp="3gm02c8nazdmr">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-5575" data-mp="3gn1q9dpf3ulw">
                    Ventas por Hora
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-5576" data-mp="3bpruzhfckoq5">
                    Distribución a lo largo del día
                  </p>
                </div>
                {hourlyChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-5577" data-mp="10hp1hrfdbzdk">
                    Sin datos
                  </div> : <div className="h-56" data-id="element-5578" data-mp="wfv8847i5xnf">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-5579" data-mp="rhjuvji0150g">
                      <BarChart data={hourlyChartData} margin={{
                  top: 4,
                  right: 4,
                  left: 0,
                  bottom: 0
                }} data-id="element-5580" data-mp="renxpxdz6kd2">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} data-id="element-5581" data-mp="2bwvll3bgflxh" />
                        <XAxis dataKey="label" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={{
                    stroke: 'rgba(255,255,255,0.06)'
                  }} data-id="element-5582" data-mp="3pt596z2ss8py" />
                        <YAxis tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={formatCompactCurrency} data-id="element-5583" data-mp="36vcj4xd9vt9z" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-5585" data-mp="v3gpaz4vvep5" />} data-id="element-5584" data-mp="v5074u66txt1" />
                        <Bar dataKey="gmv" name="GMV" fill="#3b82f6" radius={[3, 3, 0, 0]} maxBarSize={32} opacity={0.8} data-id="element-5586" data-mp="v6uusg8ug40t" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>}
              </div>

              {/* Orders Daily */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-5587" data-mp="316m1m55igj5h">
                <div className="mb-6" data-id="element-5588" data-mp="2dgrpxqwxh0mt">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-5589" data-mp="28h5d3il0hmej">
                    Órdenes Diarias
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-5590" data-mp="2n6b8swkn4uho">
                    Volumen de pedidos por día
                  </p>
                </div>
                {dailyChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-5591" data-mp="1uoyw5ua1bbe1">
                    Sin datos
                  </div> : <div className="h-56" data-id="element-5592" data-mp="58v9jgemf5fr">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-5593" data-mp="57tlmbciaogm">
                      <BarChart data={dailyChartData} margin={{
                  top: 4,
                  right: 4,
                  left: 0,
                  bottom: 0
                }} data-id="element-5594" data-mp="9zv78nvkdrjb">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} data-id="element-5595" data-mp="nyutxpzinwcu" />
                        <XAxis dataKey="label" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={{
                    stroke: 'rgba(255,255,255,0.06)'
                  }} interval="preserveStartEnd" data-id="element-5596" data-mp="exd77orlswf6" />
                        <YAxis tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} data-id="element-5597" data-mp="3f6evdh91svo0" />
                        <Tooltip content={<DarkTooltip isCurrency={false} data-id="element-5599" data-mp="6qbr7lh59p4f" />} data-id="element-5598" data-mp="6psjsyp4ej4n" />
                        <Bar dataKey="orders" name="Órdenes" fill="#8b5cf6" radius={[3, 3, 0, 0]} maxBarSize={32} opacity={0.8} data-id="element-5600" data-mp="1r8y8zefv9ct" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>}
              </div>
            </div>

            {/* ─── Charts Row 3 (Horizontal Bars) ──────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-5601" data-mp="t0k44bbmpm67">
              {/* Top Restaurants */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-5602" data-mp="xyveqo9r00ph">
                <div className="mb-6" data-id="element-5603" data-mp="11z5jjxlt0l2r">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-5604" data-mp="11y47bpt1xsjg">
                    Top Restaurantes
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-5605" data-mp="1bq9xqv3no1wj">
                    Por GMV generado
                  </p>
                </div>
                {topRestaurantsChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-5606" data-mp="1lneprbk6mrl9">
                    Sin datos
                  </div> : <div className="h-56" data-id="element-5607" data-mp="3nfax08zbhfa8">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-5608" data-mp="3ngcmhih2y4bh">
                      <BarChart data={topRestaurantsChartData} layout="vertical" margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 0
                }} data-id="element-5609" data-mp="3rgmokk1r4al2">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" horizontal={false} data-id="element-5610" data-mp="17ofaia8frv3c" />
                        <XAxis type="number" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={formatCompactCurrency} data-id="element-5611" data-mp="1hj7b7mxbey2c" />
                        <YAxis dataKey="name" type="category" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.6)'
                  }} tickLine={false} axisLine={false} width={100} data-id="element-5612" data-mp="1vcost26fy0pq" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-5614" data-mp="29yca8fl9er4w" />} data-id="element-5613" data-mp="29ym5oip2pqhz" />
                        <Bar dataKey="gmv" name="GMV" fill="#f59e0b" radius={[0, 3, 3, 0]} barSize={20} opacity={0.8} data-id="element-5615" data-mp="1w7z7qsr504my" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>}
              </div>

              {/* Top Products */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-5616" data-mp="1d9wm8nxssptw">
                <div className="mb-6" data-id="element-5617" data-mp="1i7oejj2cudym">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-5618" data-mp="1xq7hhyo1u3x5">
                    Top Productos
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-5619" data-mp="1squw7ictt4wo">
                    Por ingresos generados
                  </p>
                </div>
                {topProductsChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-5620" data-mp="1jpeltam0h2sg">
                    Sin datos
                  </div> : <div className="h-56" data-id="element-5621" data-mp="1a0wqva0expwq">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-5622" data-mp="19zv4i9dy5gzp">
                      <BarChart data={topProductsChartData} layout="vertical" margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 0
                }} data-id="element-5623" data-mp="1510m38jw5ug4">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" horizontal={false} data-id="element-5624" data-mp="r3virgbkb9ok" />
                        <XAxis type="number" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={formatCompactCurrency} data-id="element-5625" data-mp="37q1tlbywog6x" />
                        <YAxis dataKey="name" type="category" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.6)'
                  }} tickLine={false} axisLine={false} width={100} data-id="element-5626" data-mp="2szkwu5x7uegd" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-5628" data-mp="lbzxjt6nz0mi" />} data-id="element-5627" data-mp="lbq3nuii09bj" />
                        <Bar dataKey="revenue" name="Ingresos" fill="#ec4899" radius={[0, 3, 3, 0]} barSize={20} opacity={0.8} data-id="element-5629" data-mp="q9i4xf5197se" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>}
              </div>
            </div>

            {/* ─── Tables Section ─────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-5630" data-mp="1dz2r06whgl5w">
              {/* Top Products Table */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] overflow-hidden flex flex-col" data-id="element-5631" data-mp="1ixdxc7nkkdjd">
                <div className="p-5 border-b border-white/[0.06]" data-id="element-5632" data-mp="3fsa5je49uyqq">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-5633" data-mp="3kp0k1x0fhma1">
                    Detalle de Productos
                  </h2>
                </div>
                <div className="overflow-x-auto flex-1" data-id="element-5634" data-mp="3d840t47u3494">
                  <table className="w-full text-sm" data-id="element-5635" data-mp="38ej91twu36id">
                    <thead data-id="element-5636" data-mp="33ghiknx2w4wv">
                      <tr className="text-xs text-white/30 uppercase tracking-wider bg-white/[0.02]" data-id="element-5637" data-mp="33fokebc6qnqc">
                        <th className="text-left px-5 py-3 font-medium" data-id="element-5638" data-mp="2yi6aahmfm2gr">
                          Producto
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-5639" data-mp="3w4dl499bwr1k">
                          Cant.
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-5640" data-mp="3m6peom2d1v3k">
                          Ingresos
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-5641" data-mp="2arsqfee039x9">
                      {data.topProducts.slice(0, 10).map((p, i) => <tr key={i} className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors" data-id="element-5642" data-mp="25spk7qmaaxnp" data-mpi={i}>
                          <td className="px-5 py-3" data-id="element-5643" data-mp="11uvk7nurf1zz">
                            <p className="font-medium text-white/90" data-id="element-5644" data-mp="11tkkbz4fqee5">
                              {p.product_name}
                            </p>
                            {restaurantId === 'all' && <p className="text-xs text-white/40 mt-0.5" data-id="element-5645" data-mp="ssmc14myrkpr">
                                {p.restaurant_name}
                              </p>}
                          </td>
                          <td className="px-5 py-3 text-right text-white/60" data-id="element-5646" data-mp="iy45zsu2rd2s">
                            {p.quantity}
                          </td>
                          <td className="px-5 py-3 text-right font-medium text-emerald-400" data-id="element-5647" data-mp="a2wewavqamuo">
                            {formatCurrency(p.revenue)}
                          </td>
                        </tr>)}
                      {data.topProducts.length === 0 && <tr data-id="element-5648" data-mp="3cocejnkok4ar">
                          <td colSpan={3} className="px-5 py-8 text-center text-white/30" data-id="element-5649" data-mp="3cpmfckix3ql3">
                            Sin datos
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Recent Orders Table */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] overflow-hidden flex flex-col" data-id="element-5650" data-mp="3vllxz4q09hs0">
                <div className="p-5 border-b border-white/[0.06]" data-id="element-5651" data-mp="98dxi0o6wqqd">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-5652" data-mp="97m1wpulet03">
                    Órdenes Recientes
                  </h2>
                </div>
                <div className="overflow-x-auto flex-1" data-id="element-5653" data-mp="j5s4zas4y56j">
                  <table className="w-full text-sm" data-id="element-5654" data-mp="j479nixzyi7m">
                    <thead data-id="element-5655" data-mp="e8jjb07s3j0j">
                      <tr className="text-xs text-white/30 uppercase tracking-wider bg-white/[0.02]" data-id="element-5656" data-mp="e9bfac5afirc">
                        <th className="text-left px-5 py-3 font-medium" data-id="element-5657" data-mp="27pxpg7vka8m0">
                          ID / Cliente
                        </th>
                        <th className="text-left px-5 py-3 font-medium" data-id="element-5658" data-mp="2hfh6r93mluer">
                          Estado
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-5659" data-mp="2mdsixqhe8h05">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-5660" data-mp="1trhgeaa8860p">
                      {data.recentOrders.slice(0, 10).map((o, i) => <tr key={o.id || i} className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors" data-id="element-5661" data-mp="1yohqqxe3zuxm" data-mpi={i}>
                          <td className="px-5 py-3" data-id="element-5662" data-mp="3a3wi9lnjtulc">
                            <p className="font-medium text-white/90" data-id="element-5663" data-mp="3f3rdg1ecx6c7">
                              {String(o.id).substring(0, 8).toUpperCase()}
                            </p>
                            <p className="text-xs text-white/40 mt-0.5" data-id="element-5664" data-mp="13iibtsu1pjj7">
                              {o.customer_name}
                            </p>
                          </td>
                          <td className="px-5 py-3" data-id="element-5665" data-mp="torst3gou1aw">
                            <span className="px-2 py-1 rounded-md text-[10px] font-medium uppercase tracking-wider bg-white/[0.06] text-white/70" data-id="element-5666" data-mp="os1fup06jltp">
                              {o.status}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-right font-medium text-white" data-id="element-5667" data-mp="fpjt89gm4r66">
                            {formatCurrency(o.total)}
                          </td>
                        </tr>)}
                      {data.recentOrders.length === 0 && <tr data-id="element-5668" data-mp="2559fibuhgpkc">
                          <td colSpan={3} className="px-5 py-8 text-center text-white/30" data-id="element-5669" data-mp="253f3jn16w7se">
                            Sin datos
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Quick Links Section */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-5670" data-mp="3povc6vz871qc">
              <Link to="/admin/enrollment/receipts" className="bg-[#12121a] border border-white/[0.06] rounded-2xl p-6 hover:bg-white/[0.02] transition-colors group flex items-center justify-between" data-id="element-5671" data-mp="h8a199n19ykl">
                <div className="flex items-center gap-4" data-id="element-5672" data-mp="79axo5h4kt4g">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-5673" data-mp="c8e49ucr77ej">
                    <FileTextIcon className="w-6 h-6 text-emerald-400" data-id="element-5674" data-mp="2ryqov1k9ajum" />
                  </div>
                  <div data-id="element-5675" data-mp="2x0g0aibe4zj8">
                    <h3 className="text-white font-bold" data-id="element-5676" data-mp="1f1je82svftkl">Recibos de Pago</h3>
                    <p className="text-sm text-white/50 mt-0.5" data-id="element-5677" data-mp="1f3mdldfwyz1z">
                      Generar y consultar recibos
                    </p>
                  </div>
                </div>
                <ArrowLeftIcon className="w-5 h-5 text-white/30 rotate-180 group-hover:text-white/70 transition-colors" data-id="element-5678" data-mp="1o1oaphwj17jz" />
              </Link>
            </div>
          </>}
      </main>
    </div>;
}